/**
 * Roam — weekend-travel discovery across all 99 anchor host cities.
 *
 * See /app/backend/roam.py for the honesty rules this page must respect:
 *   - Real geographic distance (no fabricated "3.5h train" precision).
 *   - Cost ranges labelled as approximate.
 *   - Weekends-left = a real count from the student's own case dates.
 *   - Manifest overlap = real matches only; omit line if none.
 *   - One primary action per destination: create a real Moment (type=trip).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Users, ExternalLink, Compass, Info, ArrowRight, Sparkles, Check, PartyPopper, Calendar, Plane, TrainFront } from "lucide-react";
import { MOMENT_TAGLINE, START_MOMENT_LABEL } from "@/lib/momentCopy";

// iter92 (Item 5) · Real, verified deep-link URL patterns for "Get there"
// search entrypoints. Both patterns were tested in a real browser and
// confirmed to land on actual search-result pages (not a broken homepage
// or generic marketing page). No live prices are ever displayed to the
// student — these are pure SEARCH links, so we never show a fake or
// unsourced fare figure.
//
//   * Google Flights `?q=` parameter — verified: title reads
//     "{origin} to {destination} | Google Flights" and the page renders the
//     real flight cards for the route.
//   * Trainline route slug pages — verified: title reads
//     "{origin} to {destination} by Train from $X | Trainline"; page shows
//     real train + bus timetables and prices. Trainline aggregates both
//     train and bus operators across the EU including FlixBus.
function _flightsUrl(origin, destination) {
  if (!origin || !destination) return null;
  const q = encodeURIComponent(`Flights from ${origin} to ${destination}`);
  return `https://www.google.com/travel/flights?q=${q}`;
}
function _trainsBusesUrl(origin, destination) {
  if (!origin || !destination) return null;
  const slug = (s) => s.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return `https://www.thetrainline.com/en/train-times/${slug(origin)}-to-${slug(destination)}`;
}

export default function Roam() {
  const [data, setData] = useState(null);
  const [festivals, setFestivals] = useState(null);
  const [busyDest, setBusyDest] = useState(null);
  const [intentBusy, setIntentBusy] = useState(null);
  const [festivalBusy, setFestivalBusy] = useState(null);
  const navigate = useNavigate();

  const loadDashboard = () =>
    api.get("/roam/dashboard")
      .then(r => setData(r.data))
      .catch(() => setData({ error: true }));

  const loadFestivals = () =>
    api.get("/festivals/relevant")
      .then(r => setFestivals(r.data))
      // Gracefully degrade to public list if the user isn't authenticated
      // for the relevant-endpoint — Roam is a logged-in page, so this is
      // a rare fallback, not a normal path.
      .catch(() => api.get("/festivals").then(r => setFestivals(r.data)).catch(() => setFestivals({ error: true })));

  useEffect(() => { loadDashboard(); loadFestivals(); }, []);

  // Toggle an intent for a destination for the coming weekend. This is the
  // signal students opt into explicitly — no inference from clicks or moment
  // creation. The dashboard uses it to compute the intent-match overlap.
  const toggleIntent = async (dest, active) => {
    setIntentBusy(dest.name);
    try {
      await api.post("/roam/intent", {
        city:             dest.name,
        country:          dest.country,
        weekend_saturday: data?.next_weekend?.saturday,
        active,
      });
      if (active) {
        toast.success(`Saved. Thinking about ${dest.name}`, { duration: 2500 });
      } else {
        toast(`Removed intent for ${dest.name}`, { duration: 2000 });
      }
      await loadDashboard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your intent");
    } finally {
      setIntentBusy(null);
    }
  };

  // iter73 (P0) — Non-opted-in users clicked "Start a Moment" and got a
  // silent 403 toast that was easy to miss, so it felt like a broken button.
  // We now check `manifest_opted` from the Roam response BEFORE showing the
  // create-moment path: if not opted in, we replace the CTA with an
  // explicit "Opt in to coordinate" button that navigates to Manifest.
  const startMoment = async (dest) => {
    if (!data?.next_weekend) return;
    // Extra client-side belt-and-braces guard. The button visual already
    // switches to "Opt in to coordinate", but if a stale state somehow lets
    // us reach here, route the user to Manifest instead of hitting a 403.
    if (data.manifest_opted === false) {
      navigate("/dashboard/manifest?tab=matches");
      return;
    }
    setBusyDest(dest.name);
    try {
      const r = await api.post("/manifest/moments", {
        type:  "trip",
        title: `${dest.name} weekend trip`,
        description: `Approximate cost per person: €${dest.cost_estimate.eur_low}–€${dest.cost_estimate.eur_high}. Distance from ${data.host_city}: ~${dest.distance_km} km. Estimate. Confirm actual before booking.`,
        start_date: data.next_weekend.saturday,
        end_date:   data.next_weekend.sunday,
        date_precision: "flexible",
      });
      toast.success(`Assembly started for ${dest.name}`, { duration: 3000 });
      // The Moments view lives inside Manifest as a tab (not a standalone
      // route). Navigate there with query params so Manifest opens on the
      // Moments tab AND MomentsView auto-opens the just-created card.
      navigate(`/dashboard/manifest?tab=moments&open=${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the Assembly");
    } finally {
      setBusyDest(null);
    }
  };

  // iter78 (Item E) — Create a Moment directly from a festival card.
  // Reuses the SAME /manifest/moments endpoint as startMoment above so
  // gating (iter73/74), targeting, and lifecycle stay unified. We stamp
  // festival_id on the doc so Item F can compute a real peer-signal
  // count without any parallel data store.
  const startMomentFromFestival = async (festival) => {
    if (!festival) return;
    // Belt-and-braces gate mirror. The button visual switches to "Opt in
    // to coordinate" for non-opted users, but a stale state must not
    // reach a silent 403.
    if (data?.manifest_opted === false) {
      navigate("/dashboard/manifest?tab=matches");
      return;
    }
    setFestivalBusy(festival.id);
    try {
      // Title: "Oktoberfest — Munich" style. Trim to the 120-char limit
      // enforced by MomentCreate on the backend.
      const rawTitle = `${festival.name} - ${festival.city}`;
      const title = rawTitle.length > 120 ? rawTitle.slice(0, 117) + "..." : rawTitle;
      // If Belgium's Tomorrowland (multi-window festival), default to the
      // first window; the student can still edit dates in the Moment
      // detail once created.
      const defStart = festival.windows?.[0]?.start || festival.start_date;
      const defEnd = festival.windows?.[0]?.end || festival.end_date;
      const r = await api.post("/manifest/moments", {
        type:  "trip",
        title,
        description: `Festival trip. ${festival.description || ""} Source: ${festival.source_url || "n/a"}. Dates default to the festival window; edit if you want a shorter or longer trip.`,
        start_date: defStart,
        end_date:   defEnd,
        date_precision: "flexible",
        festival_id: festival.id,
      });
      toast.success(`Assembly started for ${festival.name}`, { duration: 3000 });
      navigate(`/dashboard/manifest?tab=moments&open=${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the Assembly");
    } finally {
      setFestivalBusy(null);
    }
  };

  if (!data) return (
    <div className="max-w-5xl mx-auto px-4 py-16" data-testid="roam-loading">
      <div className="moe-shimmer h-8 w-40 mb-6"/>
      <div className="moe-shimmer h-24 w-full mb-4"/>
      <div className="moe-shimmer h-32 w-full"/>
    </div>
  );
  if (data.error) return (
    <div className="max-w-5xl mx-auto px-4 py-16 text-muted" data-testid="roam-error">
      Couldn't load Drift right now. <Link to="/dashboard" className="underline text-elec">Back to dashboard</Link>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="roam-page">
      {/* Header */}
      <div className="mb-6">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink" data-testid="roam-back">
          <ArrowLeft size={14}/> Back to dashboard
        </Link>
        <div className="mt-4 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Drift</div>
            <h1 className="mt-2 text-3xl md:text-4xl font-bold tracking-tight text-ink">
              Weekend trips from {data.host_city || "your city"}
            </h1>
            <p className="mt-2 text-sm text-muted max-w-xl">
              A small curated set. 3-5 options within ~{data.filter?.radius_km || 300} km of your host city.
              Estimates below are approximate; confirm actual prices before booking.
            </p>
          </div>
          {/* Weekends-left. Real count from the student's case. */}
          {data.weekends_left != null && (
            <div className="rounded-2xl border border-ink/12 px-5 py-4 bg-paper" data-testid="roam-weekends-left">
              <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">Weekends left in exchange</div>
              <div className="mt-1 text-3xl font-black tabular-nums text-ink">{data.weekends_left}</div>
              <div className="text-[11px] text-muted">
                Calculated from your case dates. Not a countdown.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* iter73 (P0). Manifest opt-in gate banner. Sits at the top of Roam
         when the user isn't opted in, so the "you can't coordinate yet"
         dependency is visible BEFORE the destination cards. Also links out
         to Manifest so the user has a clear path to unlock the feature. */}
      {data.manifest_opted === false && data.in_scope !== false && (
        <div
          data-testid="roam-manifest-gate"
          className="mb-6 rounded-2xl border border-elec/40 bg-[color:var(--moe-mint)]/60 p-5 md:p-6 flex items-start gap-4"
        >
          <div className="w-10 h-10 rounded-full bg-elec/15 text-elec flex items-center justify-center shrink-0">
            <Sparkles size={16} strokeWidth={2.4}/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Coordination is gated</div>
            <div className="mt-1 text-base md:text-lg font-bold text-ink leading-snug">
              Browsing is free. To start an Assembly or coordinate a trip, opt in to Cohort first.
            </div>
            <p className="mt-1 text-sm text-muted leading-snug">
              Manifest is where you and your cohort see each other. Trip coordination lives on top of that. Opt in takes about 10 seconds and you can turn it off anytime.
            </p>
            <Link
              to="/dashboard/manifest?tab=matches"
              data-testid="roam-manifest-gate-cta"
              className="mt-3 inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-ink text-white text-sm font-semibold hover:opacity-90"
            >
              Opt in to Manifest <ArrowRight size={13}/>
            </Link>
          </div>
        </div>
      )}

      {/* Out-of-scope banner (non-anchor city) */}
      {data.in_scope === false && (
        <div className="rounded-2xl border border-ink/12 p-6 bg-tint" data-testid="roam-out-of-scope">
          <div className="flex items-start gap-3">
            <Info size={16} className="mt-0.5 text-ink/60 shrink-0"/>
            <div className="min-w-0">
              <div className="font-bold text-ink">Drift isn't scoped to {data.host_city || "your host city"} yet</div>
              <p className="mt-1 text-sm text-muted">{data.message}</p>
              <div className="mt-3 text-[11px] uppercase tracking-widest text-muted font-bold">Currently covered ({data.anchor_cities?.length || 0} cities)</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(data.anchor_cities || []).map(c => (
                  <span key={c} className="text-[11px] px-2 py-0.5 rounded-full bg-paper border border-ink/12 text-ink">{c}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Destinations */}
      {data.in_scope && (
        <div className="mt-4">
          {/* iter87 (Item X) — plain-language explainer at first touch. Same
              tagline used on the Festivals section and inside Manifest so
              "Moment" means the same thing everywhere it appears. */}
          {data.destinations?.length > 0 && (
            <p className="mb-4 text-[13px] text-muted leading-snug max-w-2xl" data-testid="roam-moment-tagline">
              {MOMENT_TAGLINE}
            </p>
          )}
          {data.destinations?.length === 0 ? (
            <div className="rounded-2xl border border-ink/12 p-8 text-muted text-sm" data-testid="roam-no-destinations">
              No destinations within {data.filter?.radius_km} km of {data.host_city} yet. This build's pool is limited on purpose. More coming.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="roam-destinations">
              {/* iter92 (Item 5a) · Budget-tier reorder. For budget-conscious
                 students, sort cheaper destinations first (by cost_estimate
                 midpoint ASC). For "flexible" / "comfort" tiers keep the
                 original curated order. Missing budget_tier → no reorder
                 (honest fallback). */}
              {(() => {
                const dests = [...(data.destinations || [])];
                if (data.budget_tier === "low") {
                  dests.sort((a, b) => {
                    const am = (a.cost_estimate?.eur_low || 0) + (a.cost_estimate?.eur_high || 0);
                    const bm = (b.cost_estimate?.eur_low || 0) + (b.cost_estimate?.eur_high || 0);
                    return am - bm;
                  });
                }
                return dests;
              })().map((d, i) => (
                <div
                  key={d.name}
                  className="rounded-2xl border border-ink/12 bg-paper p-5 md:p-6 flex flex-col"
                  data-testid={`roam-destination-${d.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">{d.country}</div>
                      <div className="mt-1 text-xl font-bold text-ink truncate">{d.name}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-muted font-bold">
                        <MapPin size={11}/> ~{d.distance_km} km
                      </div>
                    </div>
                  </div>

                  {/* Cost estimate. Clearly labelled approximate */}
                  <div className="mt-4 rounded-lg bg-ink/[0.03] p-3.5 border border-ink/8">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-ink">Approx. Weekend cost</div>
                      <div className="text-lg font-bold text-ink tabular-nums">
                        €{d.cost_estimate.eur_low}–€{d.cost_estimate.eur_high}
                      </div>
                    </div>
                    <div className="mt-1 text-[10px] leading-snug text-muted">
                      Train roundtrip €{d.cost_estimate.components.train_roundtrip_low}–€{d.cost_estimate.components.train_roundtrip_high} · 2 budget-hostel nights €{d.cost_estimate.components.accommodation_2n_low}–€{d.cost_estimate.components.accommodation_2n_high}. Approximate. Confirm actual before booking.
                    </div>
                    {/* iter92 (Item 5a) — Budget-friendly hint. Honest, single
                       line, only rendered for the two lowest-cost dests when
                       the student is on the low tier. No urgency styling. */}
                    {data.budget_tier === "low" && i < 2 && (
                      <div
                        data-testid={`roam-budget-hint-${d.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                        className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-elec"
                      >
                        <Sparkles size={10} strokeWidth={2.6}/> Budget-friendly pick for your tier
                      </div>
                    )}
                  </div>

                  {/* iter92 (Item 5b) — "Get there" search links. Real,
                     tested deep-links to Google Flights + Trainline (which
                     aggregates trains AND buses including FlixBus). No live
                     prices displayed - these are SEARCH links only, honest
                     copy. Opens in a new tab so the student doesn't lose
                     their place on Roam. */}
                  <div
                    className="mt-3 flex flex-wrap items-center gap-2"
                    data-testid={`roam-get-there-${d.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                  >
                    <span className="text-[10px] uppercase tracking-widest font-bold text-muted">Get there</span>
                    {_flightsUrl(data.host_city, d.name) && (
                      <a
                        href={_flightsUrl(data.host_city, d.name)}
                        target="_blank" rel="noopener noreferrer nofollow"
                        data-testid={`roam-flights-link-${d.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-ink border border-ink/15 rounded-full px-2.5 py-1 hover:border-elec hover:text-elec transition-colors"
                      >
                        <Plane size={11} strokeWidth={2.2}/> Search flights
                        <ExternalLink size={9} strokeWidth={2.2} className="opacity-60"/>
                      </a>
                    )}
                    {_trainsBusesUrl(data.host_city, d.name) && (
                      <a
                        href={_trainsBusesUrl(data.host_city, d.name)}
                        target="_blank" rel="noopener noreferrer nofollow"
                        data-testid={`roam-trains-link-${d.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-ink border border-ink/15 rounded-full px-2.5 py-1 hover:border-elec hover:text-elec transition-colors"
                      >
                        <TrainFront size={11} strokeWidth={2.2}/> Search trains &amp; buses
                        <ExternalLink size={9} strokeWidth={2.2} className="opacity-60"/>
                      </a>
                    )}
                  </div>

                  {/* Manifest overlap. Real count only. Intent match takes
                       precedence; the "free this weekend" line only appears
                       when there is no intent overlap (or when intent match
                       is smaller than the total availability count and adds
                       value as fallback). Never fabricated. */}
                  {d.manifest_matches_intent > 0 ? (
                    <div
                      className="mt-3 inline-flex items-center gap-1.5 text-xs text-elec font-semibold"
                      data-testid={`roam-matches-intent-${d.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                    >
                      <Sparkles size={12}/>
                      {d.manifest_matches_intent} {d.manifest_matches_intent === 1 ? "peer is" : "peers are"} also interested in {d.name}
                    </div>
                  ) : d.manifest_matches_free > 0 ? (
                    <div
                      className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted font-semibold"
                      data-testid={`roam-matches-free-${d.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                    >
                      <Users size={12}/>
                      {d.manifest_matches_free} of your matches {d.manifest_matches_free === 1 ? "is" : "are"} free that weekend
                    </div>
                  ) : null}

                  {/* Trip Intent toggle. Explicit signal only. */}
                  <div className="mt-3">
                    {d.my_intent_active ? (
                      <button
                        type="button"
                        onClick={() => toggleIntent(d, false)}
                        disabled={intentBusy === d.name}
                        data-testid={`roam-intent-clear-${d.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-elec/10 text-elec text-[11px] font-bold uppercase tracking-widest hover:bg-elec/15 disabled:opacity-50"
                      >
                        <Check size={12}/> Thinking about {d.name} · clear
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => toggleIntent(d, true)}
                        disabled={intentBusy === d.name}
                        data-testid={`roam-intent-set-${d.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-ink/15 text-ink text-[11px] font-bold uppercase tracking-widest hover:bg-ink/[0.04] disabled:opacity-50"
                      >
                        <Sparkles size={12}/> Thinking about {d.name}?
                      </button>
                    )}
                  </div>

                  {/* One-tap primary action.
                     iter73 (P0). When not opted in to Manifest, the "Start a
                     Moment" click was silently 403-ing on the backend. Replace
                     the CTA with an explicit "Opt in to coordinate" link that
                     routes to Manifest, so the dependency is visible not
                     silent. */}
                  <div className="mt-auto pt-4 flex flex-wrap gap-2 items-center justify-between">
                    <div className="text-[11px] text-muted">
                      Next weekend: {data.next_weekend?.saturday} → {data.next_weekend?.sunday}
                    </div>
                    {data.manifest_opted === false ? (
                      <Link
                        to="/dashboard/manifest?tab=matches"
                        data-testid={`roam-optin-to-coordinate-${d.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                        className="h-9 px-3.5 rounded-full border border-elec text-elec text-xs font-semibold hover:bg-elec/10 inline-flex items-center gap-1.5"
                      >
                        Opt in to coordinate <ArrowRight size={12}/>
                      </Link>
                    ) : (
                      <button
                        type="button"
                        onClick={() => startMoment(d)}
                        disabled={busyDest === d.name}
                        data-testid={`roam-start-moment-${d.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                        className="h-9 px-3.5 rounded-full bg-ink text-white text-xs font-semibold hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
                      >
                        {busyDest === d.name ? "Starting…" : (<>{START_MOMENT_LABEL} <ArrowRight size={12}/></>)}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* iter73. The footer manifest opt-in nudge that used to live here
             was moved to a prominent top gate banner (see roam-manifest-gate
             above), so users see the dependency BEFORE they scroll through
             destinations and click a gated button. */}

          {/* Cross-link to Manifest Moments */}
          <div className="mt-6 flex items-center justify-between rounded-xl border border-ink/12 p-4" data-testid="roam-cross-manifest">
            <div className="min-w-0">
              <div className="text-sm font-bold text-ink">Already coordinating a trip?</div>
              <div className="text-[12px] text-muted">Every destination card above creates a real Assembly. Same system you use in Cohort.</div>
            </div>
            <Link to="/dashboard/manifest" className="text-xs uppercase tracking-widest font-bold text-elec hover:underline inline-flex items-center gap-1 shrink-0">
              Open Manifest <ArrowRight size={12}/>
            </Link>
          </div>
        </div>
      )}

      {/* iter77 — Festivals layer. Separate from the 300km weekend-trip
         radius above: these are seasonal, city-specific events that may
         sit outside that radius and justify a longer trip. Every entry
         is cross-verified against the official organiser + tourism
         board (see /app/backend/festivals.py). No placeholders for
         unresearched countries. */}
      <FestivalsSection
        festivals={festivals}
        manifestOpted={data?.manifest_opted}
        onStartMoment={startMomentFromFestival}
        busyId={festivalBusy}
      />

      {/* Honesty footer */}
      <div className="mt-10 border-t border-ink/10 pt-6 text-[11px] text-muted leading-snug flex items-start gap-2">
        <Compass size={12} className="mt-0.5 text-ink/50 shrink-0"/>
        <div>
          Distances are Haversine great-circle from your host city. Actual train time will be longer.
          Cost figures are approximate ranges only. If any of these feels off, it probably is. Check with the operator before you buy.
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────
 * FestivalsSection — renders the verified festival list as a separate
 * layer below Roam's weekend-trip cards. Two states:
 *   - Loading skeleton
 *   - Loaded: shows any festivals overlapping the student's exchange
 *     window OR in their host country first, then all others below a
 *     "See all festivals in coverage" divider.
 *
 * Every festival card carries: name, city, exact ISO dates rendered as a
 * humanised range, one-line description, `date_note` explaining how the
 * date was derived, and a source link. Two badges:
 *   - "In your host country" — festival is in the student's host country
 *   - "Overlaps your exchange" — festival window intersects the case window
 * Never fabricated: both flags come from the backend annotation, which
 * only sets True when it has real data.
 * ───────────────────────────────────────────────────────────────────── */
function FestivalsSection({ festivals, manifestOpted, onStartMoment, busyId }) {
  if (!festivals) return (
    <section className="mt-12" data-testid="festivals-loading">
      <div className="moe-shimmer h-6 w-40 mb-4"/>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="moe-shimmer h-40 w-full"/>
        <div className="moe-shimmer h-40 w-full"/>
      </div>
    </section>
  );
  if (festivals.error) return null;

  const all = festivals.festivals || [];
  const relevant = festivals.relevant || [];
  // "Other" = everything not already surfaced in `relevant`, so the two
  // groups partition the full list with no duplication.
  const relevantIds = new Set(relevant.map(f => f.id));
  const others = all.filter(f => !relevantIds.has(f.id));

  const cardProps = { manifestOpted, onStartMoment, busyId };

  return (
    <section className="mt-12" data-testid="festivals-section">
      <div className="mb-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Festivals</div>
          <h2 className="mt-1 text-2xl md:text-3xl font-bold tracking-tight text-ink">
            Real events, verified dates
          </h2>
          <p className="mt-1 text-sm text-muted max-w-2xl leading-snug">
            Separate from weekend trips above. These are seasonal, city-specific festivals that may sit
            outside the 300&nbsp;km radius but are worth planning for.
          </p>
          <p className="mt-2 text-[13px] text-muted leading-snug max-w-2xl" data-testid="festivals-moment-tagline">
            {MOMENT_TAGLINE}
          </p>
        </div>
        <div className="text-[11px] uppercase tracking-widest text-muted font-bold" data-testid="festivals-coverage">
          {festivals.covered_countries?.length || 0} of {festivals.total_countries || 19} countries covered so far
        </div>
      </div>

      {/* Relevant to your case (host country + date overlap) */}
      {relevant.length > 0 && (
        <div className="mb-6" data-testid="festivals-relevant-group">
          <div className="mb-3 text-[11px] uppercase tracking-widest text-ink font-bold">
            Relevant to your exchange
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {relevant.map(f => <FestivalCard key={f.id} f={f} {...cardProps}/>)}
          </div>
        </div>
      )}

      {/* Everything else in the coverage */}
      {others.length > 0 && (
        <div data-testid="festivals-other-group">
          {relevant.length > 0 && (
            <div className="mb-3 text-[11px] uppercase tracking-widest text-muted font-bold">
              Elsewhere in coverage
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            {others.map(f => <FestivalCard key={f.id} f={f} {...cardProps}/>)}
          </div>
        </div>
      )}

      {/* Honest coverage note */}
      {festivals.note && (
        <div className="mt-4 text-[11px] text-muted leading-snug" data-testid="festivals-note">
          {festivals.note}
        </div>
      )}
    </section>
  );
}


/* Render a single festival card. Dates are shown as a humanised range;
 * when start_date == end_date we render a single-day format. */
function FestivalCard({ f, manifestOpted, onStartMoment, busyId }) {
  const testKey = f.id;
  const sig = f.peer_signal;    // iter78 (Item F) - real peer count or null
  const busy = busyId === f.id;
  return (
    <div
      className="rounded-2xl border border-ink/12 bg-paper p-5 md:p-6 flex flex-col"
      data-testid={`festival-card-${testKey}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">
            {f.country} · {f.city}
          </div>
          <div className="mt-1 text-xl font-bold text-ink truncate flex items-center gap-2">
            <PartyPopper size={16} className="text-elec shrink-0"/>
            <span className="truncate">{f.name}</span>
          </div>
        </div>
      </div>

      {/* Dates */}
      <div className="mt-3 inline-flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-ink font-semibold" data-testid={`festival-dates-${testKey}`}>
        <Calendar size={14} className="text-ink/60"/>
        {f.windows && f.windows.length > 1 ? (
          <FestivalMultiWindow windows={f.windows}/>
        ) : (
          <FestivalDates start={f.start_date} end={f.end_date}/>
        )}
      </div>

      {/* TBA disclaimer — only shown when the backend flagged the next
         edition as unannounced. The dates above are still the most recent
         CONFIRMED edition; this line just tells the user why they might
         see a past window. */}
      {f.next_edition_status === "tba" && (
        <div
          className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300"
          data-testid={`festival-tba-${testKey}`}
        >
          <Info size={11}/> Next edition dates not yet officially announced — showing most recent confirmed edition
        </div>
      )}

      {/* Description */}
      <p className="mt-3 text-sm text-muted leading-snug">{f.description}</p>

      {/* Date-kind note — explains how the date was derived */}
      {f.date_note && (
        <p className="mt-2 text-[11px] text-muted leading-snug italic" data-testid={`festival-date-note-${testKey}`}>
          {f.date_note}
        </p>
      )}

      {/* Relevance badges — only rendered when the backend confirmed them */}
      {(f.in_host_country || f.overlaps_exchange) && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {f.in_host_country && (
            <span
              className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-elec/10 text-elec"
              data-testid={`festival-badge-host-country-${testKey}`}
            >
              In your host country
            </span>
          )}
          {f.overlaps_exchange && (
            <span
              className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-ink/10 text-ink"
              data-testid={`festival-badge-overlaps-${testKey}`}
            >
              Overlaps your exchange
            </span>
          )}
        </div>
      )}

      {/* iter78 (Item F) - Real peer signal. Only rendered when backend
         returned a non-null peer_signal (count > 0). Never fabricated
         and never a "be the first" prompt - zero state simply shows
         nothing. */}
      {sig && sig.count > 0 && (
        <div
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-elec font-semibold"
          data-testid={`festival-peer-signal-${testKey}`}
        >
          <Users size={12}/>
          {sig.going > 0 && sig.interested > 0 ? (
            <span>{sig.count} peer{sig.count === 1 ? "" : "s"} in your cohort: {sig.going} going, {sig.interested} interested</span>
          ) : sig.going > 0 ? (
            <span>{sig.going} peer{sig.going === 1 ? " is" : "s are"} going from your cohort</span>
          ) : (
            <span>{sig.interested} peer{sig.interested === 1 ? " is" : "s are"} interested from your cohort</span>
          )}
        </div>
      )}

      {/* iter78 (Item E) - Start-a-Moment CTA. Reuses the exact same
         gating logic as Roam's weekend-trip cards: non-opted users see
         the explicit "Opt in to coordinate" link routing to Manifest
         instead of hitting a silent 403 on POST. */}
      {onStartMoment && (
        <div className="mt-4 pt-3 border-t border-ink/8">
          {manifestOpted === false ? (
            <Link
              to="/dashboard/manifest?tab=matches"
              data-testid={`festival-optin-to-coordinate-${testKey}`}
              className="h-9 px-3.5 rounded-full border border-elec text-elec text-xs font-semibold hover:bg-elec/10 inline-flex items-center gap-1.5"
            >
              Opt in to coordinate <ArrowRight size={12}/>
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => onStartMoment(f)}
              disabled={busy}
              data-testid={`festival-start-moment-${testKey}`}
              className="h-9 px-3.5 rounded-full bg-ink text-white text-xs font-semibold hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {busy ? "Starting..." : (<>{START_MOMENT_LABEL} <ArrowRight size={12}/></>)}
            </button>
          )}
          <div className="mt-2 text-[10px] text-muted leading-snug">
            Pre-fills the festival dates. Edit before or after creating - not everyone wants the full window.
          </div>
        </div>
      )}

      {/* Venue + source */}
      <div className="mt-auto pt-4 flex items-center justify-between gap-3 text-[11px] text-muted">
        {f.venue && <span className="inline-flex items-center gap-1 min-w-0 truncate"><MapPin size={11} className="shrink-0"/> <span className="truncate">{f.venue}</span></span>}
        {f.source_url && (
          <a
            href={f.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-elec font-bold uppercase tracking-widest hover:underline shrink-0"
            data-testid={`festival-source-${testKey}`}
          >
            Source <ExternalLink size={10}/>
          </a>
        )}
      </div>
    </div>
  );
}


/* Humanise ISO date ranges without importing a heavy date lib.
 * Same month/year  → "19 Sep – 4 Oct 2026" (falls to next branch if months differ)
 * Cross month      → "19 Sep – 4 Oct 2026"
 * Cross year       → "30 Jan – 9 Feb 2027" or with years if they differ
 */
function FestivalDates({ start, end }) {
  const s = new Date(start + "T00:00:00Z");
  const e = new Date(end + "T00:00:00Z");
  if (isNaN(s) || isNaN(e)) return <span>{start}{end && end !== start ? ` – ${end}` : ""}</span>;
  const opts = { day: "numeric", month: "short", timeZone: "UTC" };
  const optsFull = { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "UTC" };
  const isSameDay = start === end;
  const isSameYear = s.getUTCFullYear() === e.getUTCFullYear();
  if (isSameDay) return <span>{s.toLocaleDateString("en-GB", optsFull)}</span>;
  if (isSameYear) {
    return <span>{s.toLocaleDateString("en-GB", opts)} – {e.toLocaleDateString("en-GB", { ...opts, year: "numeric" })}</span>;
  }
  return (
    <span>
      {s.toLocaleDateString("en-GB", { ...opts, year: "numeric" })} – {e.toLocaleDateString("en-GB", { ...opts, year: "numeric" })}
    </span>
  );
}


/* Multi-window renderer for festivals that run in disjoint blocks
 * (e.g. Belgium's Tomorrowland is two separate weekends). We render
 * each window inline separated by "+" so it's visually clear the
 * festival is NOT a single continuous run. */
function FestivalMultiWindow({ windows }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1 gap-y-0.5">
      {windows.map((w, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          {i > 0 && <span className="text-ink/40 font-normal">+</span>}
          {w.label && (
            <span className="text-[10px] uppercase tracking-widest font-bold text-soft mr-1">
              {w.label}
            </span>
          )}
          <FestivalDates start={w.start} end={w.end}/>
        </span>
      ))}
    </span>
  );
}
